<script setup lang="ts">
import { formatDateTime, formatDate } from '@/composables/useFormatDate';
import { type GroupStatusEntry } from '@/infrastructure/types';
import { useFetch, usePatch, useUpdate } from '@/plugins/api';
import { useDefaultStore } from '@/stores/defaultStore';
import { computed, onMounted, ref, h } from 'vue';
import { add, formatDistanceToNow } from 'date-fns'
import { ArrowCircleRight20Filled, CheckmarkCircle20Filled, FastForward20Regular, QuestionCircle20Filled } from '@vicons/fluent';
import DatePickerComponent from './DatePickerComponent.vue';

const props = defineProps<{
  groupId: number
}>()

const defaultStore = useDefaultStore()

const entries = ref<GroupStatusEntry[]>([])
const current = computed(() => entries.value[entries.value.length - 1])
const finalStep = computed(() => defaultStore.statuses.slice(-1)[0])
const isFinalStep = computed(() => current.value?.processStep === finalStep.value.processStep)
const expectedFinish = computed(() => projectedStatuses.value?.slice(-1)[0]?.expectedFinish)

const projectedStatuses = computed(() => {
  const result: Partial<GroupStatusEntry>[] = []
  if (!current.value) return result

  const currentStep = current.value.processStep
  const futureStatuses = defaultStore.statuses.filter((status) => status.processStep > currentStep)

  futureStatuses.forEach(({ id, name: statusName, processStep, expectedDurationInWeekDays }, index) => {
    let compareDate: string | null | undefined

    if (index === 0) {
      compareDate = current.value!.expectedFinish
    } else {
      compareDate = result[index - 1].expectedFinish
    }

    result.push({
      id,
      statusName,
      processStep,
      expectedFinish: compareDate ? add(new Date(compareDate), { days: expectedDurationInWeekDays }).toISOString() : undefined
    })
  })

  return result
})

async function setStatus(newStatusId: number) {
  await usePatch(`groups/${props.groupId}/set-status?statusId=${newStatusId}`, null)

  entries.value = await useFetch('group-status-entries/' + props.groupId)
  document.dispatchEvent(new CustomEvent('reload'))
}

async function onUpdateExpectedFinish(id: number, newDate?: string) {
  await useUpdate('group-status-entries/' + id, {
    expectedFinish: newDate
  })
  getData()
  document.dispatchEvent(new CustomEvent('reload'))
}

async function getData() {
  entries.value = await useFetch('group-status-entries/' + props.groupId)
}

onMounted(async () => {
  getData()
})
</script>
  
<template>
  <n-grid :x-gap="24">
    <n-gi span="12">
      <n-card size="small">
        <n-form-item label="Current status">
          <n-flex class="grow">
            <div class="grow">
              <n-select v-if="current" v-model:value="current.statusId" :options="defaultStore.statuses" label-field="name"
                value-field="id" :on-update:value="(val: number) => setStatus(val)">
              </n-select>
            </div>
            <n-tooltip v-if="!isFinalStep && projectedStatuses[0]">
              <template #trigger>
                <n-button :render-icon="() => h(FastForward20Regular)"
                  @click="setStatus(projectedStatuses[0].id as number)"></n-button>
              </template>

              {{ projectedStatuses[0]?.statusName }}
            </n-tooltip>
          </n-flex>
        </n-form-item>
        <n-form-item v-if="!isFinalStep" label="Expected finish">
          <DatePickerComponent v-if="current" v-model:formatted-value="current.expectedFinish" type="date"
            format="dd-MM-yyyy" :on-update:formatted-value="(value?: string) => onUpdateExpectedFinish(current!.id, value)" />
          <div v-else>Unknown</div>
        </n-form-item>
        <n-statistic v-if="current?.expectedFinish" label="Expected finish">
          <span v-if="isFinalStep && current.finishedOn">Completed ({{ formatDateTime(current.finishedOn) }})</span>
          {{ formatDate(expectedFinish) }} <span v-if="expectedFinish">({{ formatDistanceToNow(new Date(expectedFinish),
            { addSuffix: true }) }})</span>
        </n-statistic>
      </n-card>
    </n-gi>
    <n-gi span="12">
      <n-card title="Timeline" size="small" style="max-height: calc(80vh)" class="overflow-y-auto">
        <n-timeline>
          <n-timeline-item v-for="item in entries.slice(0, entries.length - 1)" :key="item.id" :title="item.statusName"
            :time="item.finishedOn ? formatDateTime(item.finishedOn) : formatDate(item.expectedFinish)" type="success">
            <template #icon>
              <n-icon v-if="item.finishedOn" :size="25" :component="CheckmarkCircle20Filled"></n-icon>
              <n-icon v-else :size="25" :component="QuestionCircle20Filled"></n-icon>
            </template>
          </n-timeline-item>
          <n-timeline-item v-if="current" :title="current.statusName" :type="current.finishedOn ? 'success' : 'info'"
            line-type="dashed">
            <template #icon>
              <n-icon v-if="current.finishedOn" :size="25" :component="CheckmarkCircle20Filled"></n-icon>
              <n-icon v-else :size="25" :component="ArrowCircleRight20Filled"></n-icon>
            </template>
            <template #footer>
              <div v-if="current?.expectedFinish" class="italic">{{ formatDate(current.expectedFinish) }} ({{ formatDistanceToNow(new
                Date(current.expectedFinish as string), { addSuffix: true }) }})</div>
            </template>
          </n-timeline-item>
          <n-timeline-item v-for="item in projectedStatuses" :key="item.id" :title="item.statusName" line-type="dashed">
            <template #header>
              <n-flex align="start" justify="space-between">
                <div class="flex flex-col">
                  <span>{{ item.statusName }}</span>
                  <div v-if="item.expectedFinish" class="italic text-xs text-neutral-500 font-normal mt-1">{{ formatDate(item.expectedFinish) }}
                    ({{ formatDistanceToNow(new Date(item.expectedFinish as string), { addSuffix: true }) }})</div>
                </div>
                <n-button size="small" @click="setStatus(item.id as number)">Set as current</n-button>
              </n-flex>
            </template>
          </n-timeline-item>
        </n-timeline>
      </n-card>
    </n-gi>
  </n-grid>
</template>